import React, { useMemo, useState } from 'react';

import { showDate } from '@libs/NwMoment';

import { useUserPermission } from '@contexts/UserContext';

import { NwButton, NwLinkButton } from '@components/Gui/NwButton';
import NwClipboardCopyText from '@components/Gui/NwClipboardCopyText';
import NwIcon from '@components/Gui/NwIcon';

import ModelDataUpdatesSendRequestDrawer from '@containers/Booking/ModelDataUpdates/ModelDataUpdatesSendRequestDrawer';

import {
    ActionItem,
    ActionItemBody,
    ActionItemDescription,
    ActionItemFooter,
    ActionItemIcon,
    ExternalAccessInfo,
} from './ContactDetailsActions-ui';
import useExternalAccessActionItem from './useExternalAccessActionItem';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const EmailSelect = ({ disabled, emails, onChange, placeholder, value }) => (
    <select value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)}>
        <option value="" disabled>
            {placeholder}
        </option>
        {emails.map((email) => (
            <option key={email} value={email}>
                {email}
            </option>
        ))}
    </select>
);

const NoAccessSection = ({ title }) => (
    <div className="section">
        <div className="section-header">
            <span className="section-title">{title}</span>
        </div>
        <div className="info-grid">
            <div className="info-row">
                <span className="value">You don't have access to this section.</span>
            </div>
        </div>
    </div>
);

const ExternalAccessSection = ({
    availableEmails,
    canUpdateExternalAccess,
    externalAccessActive,
    externalAccessActivationDate,
    externalAccessEmail,
    externalAccessExists,
    hasAvailableEmails,
    isExternalAccessActionInProgress,
    isRevokingExternalAccess,
    isSendingExternalRequest,
    isUpdatingExternalAccess,
    onChangeSelectedEmail,
    onRevokeExternalAccess,
    onToggleExternalAccessUpdateForm,
    onUpdateExternalAccess,
    selectedEmail,
    showExternalAccessUpdateForm,
}) => (
    <div className="section">
        <div className="section-header">
            <span className="section-title">External Access</span>
            <span className={`status-pill ${externalAccessActive ? 'active' : 'inactive'}`}>
                {externalAccessActive ? 'active' : 'not active'}
            </span>
        </div>
        <div className="info-grid">
            {externalAccessExists ? (
                <>
                    {(externalAccessActivationDate || externalAccessActive) && (
                        <div className="info-row activation-row">
                            <span className="label">Activated on</span>
                            <span className="value">
                                {externalAccessActivationDate ? showDate(externalAccessActivationDate) : '-'}
                            </span>
                            {externalAccessActive && (
                                <NwLinkButton
                                    danger
                                    className="revoke-link"
                                    icon={byPrefixAndName.fasl['ban']}
                                    disabled={isExternalAccessActionInProgress}
                                    label={'revoke'}
                                    onClick={onRevokeExternalAccess}
                                />
                            )}
                        </div>
                    )}
                    <div className="info-row email-row">
                        <span className="label">E-mail used</span>
                        <span className="value">{externalAccessEmail || '-'}</span>
                        {externalAccessActive && (
                            <NwLinkButton
                                icon={
                                    showExternalAccessUpdateForm
                                        ? byPrefixAndName.fasl['times']
                                        : byPrefixAndName.fasl['pen']
                                }
                                disabled={!hasAvailableEmails || isExternalAccessActionInProgress}
                                label={showExternalAccessUpdateForm ? 'close' : 'change'}
                                onClick={onToggleExternalAccessUpdateForm}
                            />
                        )}
                    </div>
                    {externalAccessActive && showExternalAccessUpdateForm && (
                        <div className="request-row">
                            <EmailSelect
                                value={selectedEmail}
                                disabled={isExternalAccessActionInProgress}
                                onChange={onChangeSelectedEmail}
                                placeholder="select new e-mail"
                                emails={availableEmails}
                            />
                            <NwButton
                                primary
                                loading={isUpdatingExternalAccess}
                                disabled={
                                    !canUpdateExternalAccess || isSendingExternalRequest || isRevokingExternalAccess
                                }
                                label={'update'}
                                onClick={onUpdateExternalAccess}
                            />
                        </div>
                    )}
                </>
            ) : null}
        </div>
    </div>
);

const SentUpdateRequestShare = ({ onOpenReminderDrawer, updateFormUrl, updateRequestEmailHref }) => (
    <div className="update-form-share">
        <p className="update-form-help">
            This update form is ready to share. Copy the link if you want to send it manually, or open your e-mail app
            with a reminder already addressed to the model. Use this when the model did not receive the first e-mail or
            when the booker wants to send a follow-up.
        </p>
        <NwLinkButton
            icon={byPrefixAndName.fasl['alarm-clock']}
            label={'send reminder'}
            onClick={onOpenReminderDrawer}
        />
        {updateFormUrl ? (
            <NwClipboardCopyText text={updateFormUrl} fullWidth={false} />
        ) : (
            <span className="status-error">Update form link unavailable.</span>
        )}
        {/* <div className="update-form-actions">
            <NwButton
                className="update-request-email-button"
                icon={byPrefixAndName.fasl['envelope']}
                label="CREATE E-MAIL"
                primary
                disabled={!updateRequestEmailHref}
                href={updateRequestEmailHref || undefined}
            />
        </div> */}
    </div>
);

const DataUpdateRequestSection = ({
    availableEmails,
    canSendUpdateRequest,
    externalAccessExists,
    externalAccessUpdateRequest,
    hasImportedUpdateRequest,
    hasPendingSubmittedUpdateRequest,
    hasSubmittedUpdateRequest,
    importedSummary,
    isExternalAccessActionInProgress,
    isRevokingExternalAccess,
    isSentUpdateRequest,
    isSendingExternalRequest,
    isUpdatingExternalAccess,
    onChangeSelectedEmail,
    onEnableAndOpenSendUpdateRequestDrawer,
    onOpenSubmittedUpdateReview,
    selectedEmail,
    sentSummary,
    updateFormUrl,
    updateRequestBadge,
    updateRequestEmailHref,
    onOpenReminderDrawer,
}) => (
    <div className="section">
        <div className="section-header">
            <span className="section-title">Data Update Request</span>
            {updateRequestBadge && (
                <span className={`status-pill ${updateRequestBadge.className}`}>{updateRequestBadge.label}</span>
            )}
        </div>
        <div className="info-grid">
            {externalAccessUpdateRequest ? (
                <>
                    <div className="info-row">
                        <span className="label">Sent on</span>
                        <span className="value">{sentSummary}</span>
                    </div>
                    {hasSubmittedUpdateRequest && (
                        <div className="info-row">
                            <span className="label">Update submitted by model on</span>
                            <span className="value">
                                {externalAccessUpdateRequest.SubmissionDate
                                    ? showDate(externalAccessUpdateRequest.SubmissionDate)
                                    : '-'}
                            </span>
                        </div>
                    )}
                    {hasImportedUpdateRequest && (
                        <div className="info-row">
                            <span className="label">Imported on</span>
                            <span className="value">{importedSummary}</span>
                        </div>
                    )}
                    {isSentUpdateRequest && (
                        <SentUpdateRequestShare
                            onOpenReminderDrawer={onOpenReminderDrawer}
                            updateFormUrl={updateFormUrl}
                            updateRequestEmailHref={updateRequestEmailHref}
                        />
                    )}
                </>
            ) : (
                <div className="info-row">
                    <span className="value">
                        No update requests sent.
                        <br />
                        Select an e-mail to send a new update request.
                        <br />
                        <br />
                    </span>
                </div>
            )}
        </div>
        {hasPendingSubmittedUpdateRequest && (
            <div className="request-row">
                <NwLinkButton label={'open update review'} onClick={onOpenSubmittedUpdateReview} />
            </div>
        )}
        {!externalAccessExists && (
            <div className="request-row">
                <EmailSelect
                    value={selectedEmail}
                    disabled={isExternalAccessActionInProgress}
                    onChange={onChangeSelectedEmail}
                    placeholder="select e-mail"
                    emails={availableEmails}
                />
                <NwButton
                    primary
                    loading={isSendingExternalRequest}
                    disabled={
                        !selectedEmail || !canSendUpdateRequest || isUpdatingExternalAccess || isRevokingExternalAccess
                    }
                    icon={byPrefixAndName.fasl['pen-field']}
                    label={'send'}
                    onClick={onEnableAndOpenSendUpdateRequestDrawer}
                />
            </div>
        )}
    </div>
);

const ExternalAccessActionItem = ({
    canAccessDataUpdateRequest,
    canGrantExternalAccess,
    contactId,
    defaultEmail,
    onRequestClose,
}) => {
    const hasGrantExternalAccessPermission = useUserPermission('GrantExternalAccess');
    const hasDataUpdateRequestPermission = useUserPermission('DataUpdateRequest');
    const canShowExternalAccessSection = useMemo(
        () => (typeof canGrantExternalAccess === 'boolean' ? canGrantExternalAccess : hasGrantExternalAccessPermission),
        [canGrantExternalAccess, hasGrantExternalAccessPermission]
    );
    const canShowDataUpdateRequestSection = useMemo(
        () =>
            typeof canAccessDataUpdateRequest === 'boolean'
                ? canAccessDataUpdateRequest
                : hasDataUpdateRequestPermission,
        [canAccessDataUpdateRequest, hasDataUpdateRequestPermission]
    );
    const {
        actionError,
        actionSuccess,
        availableEmails,
        canSendUpdateRequest,
        canUpdateExternalAccess,
        externalAccessActive,
        externalAccessActivationDate,
        externalAccessEmail,
        externalAccessExists,
        externalAccessLoadError,
        externalAccessUpdateRequest,
        handleChangeSelectedEmail,
        handleCloseSendUpdateRequestDrawer,
        handleEnableAndOpenSendUpdateRequestDrawer,
        handleOpenSubmittedUpdateReview,
        handleRevokeExternalAccess,
        handleSendUpdateRequest,
        handleToggleExternalAccessUpdateForm,
        handleUpdateExternalAccess,
        hasAvailableEmails,
        hasImportedUpdateRequest,
        hasPendingSubmittedUpdateRequest,
        hasSubmittedUpdateRequest,
        importedSummary,
        isExternalAccessActionInProgress,
        isLoadingExternalAccess,
        isRevokingExternalAccess,
        isSentUpdateRequest,
        isSendingExternalRequest,
        isUpdatingExternalAccess,
        selectedEmail,
        sentSummary,
        showExternalAccessUpdateForm,
        showSendUpdateRequestDrawer,
        updateFormUrl,
        updateRequestBadge,
        updateRequestEmailHref,
    } = useExternalAccessActionItem({
        contactId,
        defaultEmail,
        onRequestClose,
    });
    const [showReminderDrawer, setShowReminderDrawer] = useState(false);

    const showSendUpdateRequestFooter =
        !isLoadingExternalAccess &&
        !externalAccessLoadError &&
        canShowDataUpdateRequestSection &&
        externalAccessExists &&
        externalAccessActive &&
        canSendUpdateRequest &&
        !isSentUpdateRequest;

    const content = (
        <ActionItem>
            <ActionItemIcon>
                <NwIcon size="3x" icon={byPrefixAndName.fasl['arrow-right-to-bracket']} />
            </ActionItemIcon>
            <ActionItemBody>
                <ActionItemDescription>
                    <ExternalAccessInfo>
                        {isLoadingExternalAccess ? (
                            <span>Loading...</span>
                        ) : (
                            <>
                                {externalAccessLoadError ? (
                                    <span className="status-error">{externalAccessLoadError}</span>
                                ) : (
                                    <>
                                        {canShowExternalAccessSection ? (
                                            <ExternalAccessSection
                                                availableEmails={availableEmails}
                                                canUpdateExternalAccess={canUpdateExternalAccess}
                                                externalAccessActive={externalAccessActive}
                                                externalAccessActivationDate={externalAccessActivationDate}
                                                externalAccessEmail={externalAccessEmail}
                                                externalAccessExists={externalAccessExists}
                                                hasAvailableEmails={hasAvailableEmails}
                                                isExternalAccessActionInProgress={isExternalAccessActionInProgress}
                                                isRevokingExternalAccess={isRevokingExternalAccess}
                                                isSendingExternalRequest={isSendingExternalRequest}
                                                isUpdatingExternalAccess={isUpdatingExternalAccess}
                                                onChangeSelectedEmail={handleChangeSelectedEmail}
                                                onRevokeExternalAccess={handleRevokeExternalAccess}
                                                onToggleExternalAccessUpdateForm={handleToggleExternalAccessUpdateForm}
                                                onUpdateExternalAccess={handleUpdateExternalAccess}
                                                selectedEmail={selectedEmail}
                                                showExternalAccessUpdateForm={showExternalAccessUpdateForm}
                                            />
                                        ) : (
                                            <NoAccessSection title="External Access" />
                                        )}
                                        {canShowDataUpdateRequestSection ? (
                                            <DataUpdateRequestSection
                                                availableEmails={availableEmails}
                                                canSendUpdateRequest={canSendUpdateRequest}
                                                externalAccessExists={externalAccessExists}
                                                externalAccessUpdateRequest={externalAccessUpdateRequest}
                                                hasImportedUpdateRequest={hasImportedUpdateRequest}
                                                hasPendingSubmittedUpdateRequest={hasPendingSubmittedUpdateRequest}
                                                hasSubmittedUpdateRequest={hasSubmittedUpdateRequest}
                                                importedSummary={importedSummary}
                                                isExternalAccessActionInProgress={isExternalAccessActionInProgress}
                                                isRevokingExternalAccess={isRevokingExternalAccess}
                                                isSentUpdateRequest={isSentUpdateRequest}
                                                isSendingExternalRequest={isSendingExternalRequest}
                                                isUpdatingExternalAccess={isUpdatingExternalAccess}
                                                onChangeSelectedEmail={handleChangeSelectedEmail}
                                                onEnableAndOpenSendUpdateRequestDrawer={
                                                    handleEnableAndOpenSendUpdateRequestDrawer
                                                }
                                                onOpenSubmittedUpdateReview={handleOpenSubmittedUpdateReview}
                                                onOpenReminderDrawer={() => setShowReminderDrawer(true)}
                                                selectedEmail={selectedEmail}
                                                sentSummary={sentSummary}
                                                updateFormUrl={updateFormUrl}
                                                updateRequestBadge={updateRequestBadge}
                                                updateRequestEmailHref={updateRequestEmailHref}
                                            />
                                        ) : (
                                            <NoAccessSection title="Data Update Request" />
                                        )}
                                    </>
                                )}
                                {actionSuccess && <span className="status-success">{actionSuccess}</span>}
                                {actionError && <span className="status-error">{actionError}</span>}
                            </>
                        )}
                    </ExternalAccessInfo>
                </ActionItemDescription>
                {showSendUpdateRequestFooter && (
                    <ActionItemFooter>
                        <NwButton
                            primary
                            loading={isSendingExternalRequest}
                            disabled={isUpdatingExternalAccess || isRevokingExternalAccess}
                            icon={byPrefixAndName.fasl['pen-field']}
                            label={'send update request'}
                            onClick={handleSendUpdateRequest}
                        />
                    </ActionItemFooter>
                )}
            </ActionItemBody>
        </ActionItem>
    );

    return (
        <>
            {content}
            {showSendUpdateRequestDrawer && (
                <ModelDataUpdatesSendRequestDrawer contactId={contactId} onClose={handleCloseSendUpdateRequestDrawer} />
            )}
            {showReminderDrawer && (
                <ModelDataUpdatesSendRequestDrawer
                    contactId={contactId}
                    isReminder
                    onClose={() => setShowReminderDrawer(false)}
                />
            )}
        </>
    );
};

export default ExternalAccessActionItem;
